import * as React from "react"
import { AgGridReact } from "ag-grid-react"
import { ColDef, GridOptions, GridReadyEvent, ModuleRegistry, CellValueChangedEvent } from "ag-grid-community"
import { themeAlpine } from 'ag-grid-community';
import { cn } from "../../lib/utils"
import { AllEnterpriseModule, LicenseManager, RowGroupingModule, RowGroupingPanelModule } from "ag-grid-enterprise"

ModuleRegistry.registerModules([
    AllEnterpriseModule,
    RowGroupingModule,
    RowGroupingPanelModule
]);

const agGridLicenseKey = "Using_this_{AG_Grid}_Enterprise_key_{AG-104214}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{UEnetworks_LTD}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Upper_Echelon}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Upper_Echelon}_need_to_be_licensed___{Upper_Echelon}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{30_September_2026}____[v3]_[01]_MTc5MDcyMjgwMDAwMA==55033d4e243ca33b7b6071e4c0ef05a4";
if (agGridLicenseKey) {
  LicenseManager.setLicenseKey(agGridLicenseKey);
} else {
  console.warn("AG Grid Enterprise license key is not set. Please set the VITE_AG_GRID_LICENSE environment variable.");
}

const theme = themeAlpine.withParams({
    rowBorder: { style: 'solid',  color: '#D4D4D4' },
    columnBorder: { style: 'solid', color: '#D4D4D4' },
    headerColumnBorder: { color: '#D4D4D4' },

    rangeSelectionBorderColor: '#0091ea',
    rangeSelectionBackgroundColor: 'rgba(0, 145, 234, 0.1)',
});

export interface DataGridProps {
  columnDefs?: ColDef[]
  rowData: any[]
  className?: string
  height?: string | number
  defaultColDef?: ColDef
  gridOptions?: GridOptions
  onGridReady?: (event: GridReadyEvent) => void
  onCellValueChanged?: (event: CellValueChangedEvent) => void
  enableExport?: boolean
  rowSelection?: 'single' | 'multiple' | undefined
  animateRows?: boolean
  pagination?: boolean
  paginationPageSize?: number
  domLayout?: 'normal' | 'autoHeight' | 'print'
}

const DataGrid = React.forwardRef<AgGridReact, DataGridProps>(
  ({ 
    columnDefs,
    rowData,
    className,
    height = 400,
    defaultColDef = {
      sortable: false,
      filter: false,
      resizable: false,
      suppressHeaderFilterButton: true,
      suppressHeaderMenuButton: true,
      flex: 1,
      minWidth: 100,
    },
    gridOptions = {},
    onGridReady,
    enableExport = true,
    rowSelection,
    animateRows = true,
    pagination = false,
    paginationPageSize = 50,
    domLayout = 'normal',
    ...props
  }, ref) => {
    const containerStyle = React.useMemo(() => ({
      width: '100%',
      height: typeof height === 'number' ? `${height}px` : height,
    }), [height])

    const mergedGridOptions: GridOptions = {
      rowSelection,
      animateRows,
      pagination,
      paginationPageSize,
      domLayout,
      theme: theme,
      cellSelection: true,
      ...gridOptions
    }

    const mergedDefaultColDef = {
      ...defaultColDef
    }

    const handleGridReady = (event: GridReadyEvent) => {
      onGridReady?.(event)
    }

    return (
      <div 
        className={cn(
          "select-none",
          className
        )} 
        style={containerStyle}
      >
        <AgGridReact
          ref={ref}
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={mergedDefaultColDef}
          gridOptions={mergedGridOptions}
          onGridReady={handleGridReady}
          {...props}
        />
      </div>
    )
  }
)

DataGrid.displayName = "DataGrid"

export { DataGrid }
